import {
  createElement,
  Fragment,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createRoot, Root } from 'react-dom/client'
import { FormattedMessage, useIntl } from 'react-intl'
import { autocomplete, GetSources } from '@algolia/autocomplete-js'
import clsx from 'clsx'
import { isEmpty } from 'lodash-es'

import { debouncePromise } from 'Utilities'

import {
  DataSelectInputAsyncGetSourcesResponse,
  DataSelectInputAsyncOption,
  DataSelectInputAsyncProps,
  DataSelectInputAsyncSource,
} from './DataSelectInputAsync.types'

export const DataSelectInputAsync = memo((props: DataSelectInputAsyncProps) => {
  const {
    fetchSelectOptions,
    renderNoResults,
    renderNoResultsInvalid,
    renderOptionItem,
    sourceId,
    placeholderInputText,
    containerCssClasses,
    labelTranslationId,
    onOptionSelectionHandler,
    onInputResetHandler,
    preSelectedOption,
    disableCleaningInputValue,
    dataTestId,
    minCharacterLength = 1,
    hasError,
    errorText,
    algoliaClassNames,
  } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const panelRootRef = useRef<Root | null>(null)
  const rootRef = useRef<HTMLElement | null>(null)
  const intl = useIntl()

  const [isError, setIsError] = useState(hasError)
  const [errorMessage, setErrorMessage] = useState(errorText || '')

  const noResultsDefaultText = useMemo(
    () => intl.formatMessage({ id: 'DataSelectInputAsync_NoResult' }),
    [intl],
  )

  const fetchErrorText = useMemo(
    () => intl.formatMessage({ id: 'DataSelectInputAsync_FetchError' }),
    [intl],
  )

  useEffect(() => {
    if (hasError && errorText) {
      setIsError(hasError)
      setErrorMessage(errorText)
    }
  }, [errorText, hasError])

  useEffect(() => {
    if (!containerRef.current) {
      return undefined
    }

    const renderSources = (
      selectOptionsResponse: DataSelectInputAsyncOption[],
      isInvalid?: boolean,
    ): DataSelectInputAsyncGetSourcesResponse => [
      {
        getItemInputValue: ({ item }) => item.name,
        getItems: ({ query }) => {
          if (isEmpty(selectOptionsResponse)) {
            return []
          }

          const filteredResult = selectOptionsResponse.filter(selectOption => {
            if (selectOption.displayName) {
              return selectOption.displayName
                .toLowerCase()
                .includes(query.toLowerCase())
            }

            return selectOption.name.toLowerCase().includes(query.toLowerCase())
          })

          return filteredResult
        },
        onSelect: ({ item }) => {
          if (onOptionSelectionHandler) {
            onOptionSelectionHandler(item)
          }
        },
        sourceId,
        templates: {
          item: ({ item }) => {
            if (renderOptionItem) {
              return renderOptionItem(item)
            }

            if (item.displayName) {
              return item.displayName
            }

            return item.name
          },
          noResults: ({ state }) => {
            if (!isInvalid && renderNoResults) {
              return renderNoResults(state.query)
            }

            if (isInvalid && renderNoResultsInvalid) {
              return renderNoResultsInvalid(minCharacterLength)
            }

            return noResultsDefaultText
          },
        },
      },
    ]

    const getSources: GetSources<DataSelectInputAsyncOption> = ({ query }) => {
      setIsError(false)

      if (query.length < minCharacterLength) {
        return renderSources([], true)
      }

      return fetchSelectOptions(query)
        .then(response => renderSources(response))
        .catch(() => {
          setIsError(true)
          setErrorMessage(fetchErrorText)

          return renderSources([])
        })
    }

    const debouncedSources = debouncePromise<
      GetSources<DataSelectInputAsyncOption>,
      DataSelectInputAsyncSource
    >(getSources, 500)

    const search = autocomplete({
      classNames: {
        ...algoliaClassNames,
        panel: clsx('DataSelectInputAsync-Panel', algoliaClassNames?.panel),
      },
      container: containerRef.current,
      detachedMediaQuery: 'none',
      getSources: debouncedSources,
      onReset: () => {
        if (onInputResetHandler) {
          onInputResetHandler()
        }
      },
      onSubmit: ({ setIsOpen, refresh, state, setQuery }) => {
        if (state.isOpen) {
          setIsOpen(false)
          setQuery('')
        } else {
          setIsOpen(true)
        }

        refresh()
      },
      openOnFocus: true,
      placeholder: placeholderInputText,
      render({ children }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root

          panelRootRef.current?.unmount()
          panelRootRef.current = createRoot(root)
        }

        panelRootRef?.current?.render(children)
      },
      renderer: { Fragment, createElement, render: () => {} },
    })

    const outsideClickHandler = (event: Event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        search.setIsOpen(false)
      }
    }

    document.addEventListener('click', outsideClickHandler)

    return () => {
      search.destroy()
      document.removeEventListener('click', outsideClickHandler)
    }
  }, [
    noResultsDefaultText,
    placeholderInputText,
    renderNoResults,
    renderOptionItem,
    sourceId,
    onOptionSelectionHandler,
    onInputResetHandler,
    preSelectedOption,
    disableCleaningInputValue,
    fetchSelectOptions,
    minCharacterLength,
    renderNoResultsInvalid,
    fetchErrorText,
    algoliaClassNames,
  ])

  return (
    <div
      className={clsx('DataSelectInputAsync', containerCssClasses, {
        'DataSelectInputAsync-danger': isError,
      })}
      data-testid={dataTestId}
    >
      {labelTranslationId && (
        <p className="form-label">
          <FormattedMessage id={labelTranslationId} />
        </p>
      )}

      <div ref={containerRef} />
      {isError && errorMessage && <p className="text-danger">{errorMessage}</p>}
    </div>
  )
})
